import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../contexts/AuthContext";
import { Eye, EyeOff, Zap, Lock, Mail, User, Home, ArrowLeft, Check } from "lucide-react";
import axios from "axios";

export default function Auth({ mode = "login" }) {
  const { login, register, formatError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("form"); // "form" | "forgot" | "forgot-sent"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const isLogin = mode === "login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
      } else {
        await register(form.email, form.password, form.name);
      }
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setView("forgot-sent");
    } catch {
      setView("forgot-sent"); // Don't reveal if email exists
    } finally {
      setForgotLoading(false);
    }
  };

  const T = { fontFamily: "'Inter Tight', sans-serif" };
  const S = { fontFamily: "'IBM Plex Sans', sans-serif" };
  const L = { fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", letterSpacing: "0.16em", color: "#475569", textTransform: "uppercase", marginBottom: "6px", display: "block" };

  return (
    <div className="min-h-screen flex items-center justify-center relative" style={{ background: "#0B0D10" }} data-testid="auth-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 ace-bg-radial" />
        <div className="ace-noise" />
      </div>

      {/* Home button — fixed top-left */}
      <Link to="/" data-testid="home-button" className="fixed top-6 left-6 z-50 flex items-center gap-2 px-3 py-2 transition-all hover:bg-white/[0.03]"
        style={{ clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <Home size={14} style={{ color: "#94A3B8" }} />
        <span style={{ ...T, fontSize: "10px", fontWeight: "600", color: "#94A3B8", letterSpacing: "0.08em" }}>HOME</span>
      </Link>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="relative w-full max-w-sm mx-4">
        <div className="octa-clip" style={{ background: "rgba(255,255,255,0.045)", padding: "1px" }}>
          <div className="octa-clip p-8" style={{ background: "linear-gradient(180deg, rgba(22,25,32,1) 0%, rgba(15,17,21,1) 100%)" }}>
            <div className="absolute top-0 left-0 right-0" style={{ height: "1px", background: "linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.06) 50%, transparent 90%)" }} />

            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-9 h-9 octa-clip-sm bg-[#2563EB] flex items-center justify-center">
                <Zap size={18} className="text-white" strokeWidth={2} />
              </div>
              <div>
                <h1 style={{ ...T, fontWeight: "700", color: "#fff", fontSize: "16px", letterSpacing: "0.15em", textTransform: "uppercase" }}>ACE OS</h1>
                <p style={{ ...T, color: "#475569", fontSize: "10px", letterSpacing: "0.1em", fontWeight: "500" }}>Trading Command System</p>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {/* ─── Main Login/Signup Form ─── */}
              {view === "form" && (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                  <h2 style={{ ...T, fontWeight: "600", color: "#E2E8F0", fontSize: "18px", marginBottom: "4px" }}>
                    {isLogin ? "Sign In" : "Create Account"}
                  </h2>
                  <p style={{ ...S, color: "#475569", fontSize: "13px", marginBottom: "24px" }}>
                    {isLogin ? "Access your command center" : "Join ACE OS today"}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
                    {!isLogin && (
                      <div>
                        <label style={L}>Full Name</label>
                        <div className="relative">
                          <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Your Name" required data-testid="signup-name-input"
                            className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm pl-9 pr-3 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/40 focus:outline-none transition-all"
                            style={S} />
                        </div>
                      </div>
                    )}
                    <div>
                      <label style={L}>Email</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="trader@example.com" required data-testid="email-input"
                          className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm pl-9 pr-3 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/40 focus:outline-none transition-all"
                          style={S} />
                      </div>
                    </div>
                    <div>
                      <label style={L}>Password</label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                        <input type={showPass ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                          placeholder="Enter password" required data-testid="password-input"
                          className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm pl-9 pr-10 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/40 focus:outline-none transition-all"
                          style={S} />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#94A3B8]">
                          {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>

                    {error && (
                      <div data-testid="auth-error" className="octa-clip-sm text-xs p-3" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", color: "#EF4444", ...S }}>
                        {error}
                      </div>
                    )}

                    <button type="submit" disabled={loading} data-testid="auth-submit-btn"
                      className="w-full octa-clip-sm text-white py-3 transition-all duration-150 disabled:opacity-50 mt-2"
                      style={{ background: "#2563EB", ...T, fontWeight: "600", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {loading ? "Authenticating..." : isLogin ? "Enter Command Center" : "Create Account"}
                    </button>
                  </form>

                  {/* Forgot password link — login only */}
                  {isLogin && (
                    <button onClick={() => setView("forgot")} data-testid="forgot-password-link"
                      className="block w-full text-center text-xs mt-3 transition-colors hover:text-[#3B82F6]"
                      style={{ ...S, color: "#475569" }}>
                      Forgot password?
                    </button>
                  )}

                  <p className="text-center text-xs mt-4" style={{ ...S, color: "#475569" }}>
                    {isLogin ? "New to ACE OS? " : "Already have an account? "}
                    <Link to={isLogin ? "/signup" : "/login"} data-testid="auth-toggle-link" className="text-[#2563EB] hover:text-[#3B82F6] transition-colors">
                      {isLogin ? "Create Account" : "Sign In"}
                    </Link>
                  </p>
                </motion.div>
              )}

              {/* ─── Forgot Password Form ─── */}
              {view === "forgot" && (
                <motion.div key="forgot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                  <button onClick={() => setView("form")} data-testid="back-to-login"
                    className="flex items-center gap-1.5 mb-5 transition-colors hover:text-white"
                    style={{ ...T, fontSize: "10px", fontWeight: "600", color: "#475569", letterSpacing: "0.06em" }}>
                    <ArrowLeft size={12} /> Back to Sign In
                  </button>

                  <h2 style={{ ...T, fontWeight: "600", color: "#E2E8F0", fontSize: "18px", marginBottom: "4px" }}>Reset Password</h2>
                  <p style={{ ...S, color: "#475569", fontSize: "13px", marginBottom: "24px" }}>
                    Enter your email and we'll send a reset link
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
                    <div>
                      <label style={L}>Email</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                        <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="trader@example.com" required data-testid="forgot-email-input"
                          className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm pl-9 pr-3 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/40 focus:outline-none transition-all"
                          style={S} />
                      </div>
                    </div>

                    <button type="submit" disabled={forgotLoading} data-testid="forgot-submit-btn"
                      className="w-full octa-clip-sm text-white py-3 transition-all duration-150 disabled:opacity-50"
                      style={{ background: "#2563EB", ...T, fontWeight: "600", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {forgotLoading ? "Sending..." : "Send Reset Link"}
                    </button>
                  </form>
                </motion.div>
              )}

              {/* ─── Forgot Password Sent Confirmation ─── */}
              {view === "forgot-sent" && (
                <motion.div key="sent" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
                  className="text-center py-4">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)" }}>
                    <Check size={20} style={{ color: "#22C55E" }} />
                  </div>
                  <h2 style={{ ...T, fontWeight: "600", color: "#E2E8F0", fontSize: "16px", marginBottom: "6px" }}>Check Your Email</h2>
                  <p style={{ ...S, color: "#64748B", fontSize: "13px", marginBottom: "20px", lineHeight: 1.5 }}>
                    If an account exists for <strong style={{ color: "#94A3B8" }}>{forgotEmail}</strong>, a reset link has been sent.
                  </p>
                  <button onClick={() => { setView("form"); setForgotEmail(""); }} data-testid="back-to-login-after-reset"
                    className="octa-clip-sm text-white px-6 py-2.5 transition-all"
                    style={{ background: "#2563EB", ...T, fontWeight: "600", fontSize: "11px", letterSpacing: "0.1em" }}>
                    BACK TO SIGN IN
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
